import { ChatMessage } from '../../../models/clinicAgent';
import { Conversation } from '../../../models/agentConversation';

export const TITLE_PREVIEW_CHARS = 60;

export function truncate(text: string, max = TITLE_PREVIEW_CHARS): string {
  const t = text.trim().replace(/\s+/g, ' ');
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

// The title to show, and whether it is still a placeholder: until the backend
// titles a conversation, it shows its first message (or the newest preview).
export function displayTitle(
  c: Conversation,
  firstUserText?: string,
): { text: string; pending: boolean } {
  if (c.titleSource !== 'pending' && c.title.trim()) return { text: c.title, pending: false };
  const fallback =
    firstUserText?.trim() ||
    (c.lastMessagePreview?.role === 'user' ? c.lastMessagePreview.text : '') ||
    'Nueva conversación';
  return { text: truncate(fallback), pending: true };
}

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export type DateGroupLabel = 'Hoy' | 'Ayer' | 'Últimos 7 días' | 'Últimos 30 días' | 'Anteriores';

function dateGroup(iso: string | null, now: Date): DateGroupLabel {
  const t = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(t)) return 'Anteriores';
  const days = Math.floor((startOfDay(now) - startOfDay(new Date(t))) / DAY_MS);
  if (days <= 0) return 'Hoy';
  if (days === 1) return 'Ayer';
  if (days <= 7) return 'Últimos 7 días';
  if (days <= 30) return 'Últimos 30 días';
  return 'Anteriores';
}

const DATE_ORDER: DateGroupLabel[] = ['Hoy', 'Ayer', 'Últimos 7 días', 'Últimos 30 días', 'Anteriores'];

const activity = (c: Conversation) => {
  const t = Date.parse(c.lastMessageAt ?? c.createdAt);
  return Number.isNaN(t) ? 0 : t;
};

// Newest first, ties by id, so the order never depends on how the list was built.
const newestFirst = (a: Conversation, b: Conversation) => activity(b) - activity(a) || b.id - a.id;

export type ConversationGroup = { key: string; label: string; conversations: Conversation[] };

// One group per date range, in a fixed order: a conversation appears once,
// whatever order the list arrives in.
export function groupByDate(conversations: Conversation[], now = new Date()): ConversationGroup[] {
  const byLabel = new Map<DateGroupLabel, Conversation[]>();
  for (const c of conversations) {
    const label = dateGroup(c.lastMessageAt ?? c.createdAt, now);
    byLabel.set(label, [...(byLabel.get(label) ?? []), c]);
  }
  return DATE_ORDER.filter((l) => byLabel.has(l)).map((label) => ({
    key: label,
    label,
    conversations: byLabel.get(label)!.sort(newestFirst),
  }));
}

export const GENERAL_GROUP_LABEL = 'General';

// One group per patient, the most recently active patient first; general
// conversations last, under "General".
export function groupByPatient(conversations: Conversation[]): ConversationGroup[] {
  const byPatient = new Map<number, ConversationGroup>();
  const general: Conversation[] = [];
  for (const c of conversations) {
    if (!c.patient) {
      general.push(c);
      continue;
    }
    const g = byPatient.get(c.patient.id) ?? { key: `p${c.patient.id}`, label: c.patient.name, conversations: [] };
    g.conversations.push(c);
    byPatient.set(c.patient.id, g);
  }
  const groups = Array.from(byPatient.values());
  groups.forEach((g) => g.conversations.sort(newestFirst));
  groups.sort((a, b) => newestFirst(a.conversations[0], b.conversations[0]));
  if (general.length) groups.push({ key: 'general', label: GENERAL_GROUP_LABEL, conversations: general.sort(newestFirst) });
  return groups;
}

// Short time for a list row: "ahora", "hace 5 min", "10:42", "ayer", "12 oct".
export function relativeWhen(iso: string | null, now = new Date()): string {
  const t = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(t)) return '';
  const diff = now.getTime() - t;
  if (diff < 60_000) return 'ahora';
  if (diff < 60 * 60_000) return `hace ${Math.floor(diff / 60_000)} min`;
  const d = new Date(t);
  const days = Math.floor((startOfDay(now) - startOfDay(d)) / DAY_MS);
  if (days <= 0) return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  if (days === 1) return 'ayer';
  if (days < 7) return d.toLocaleDateString('es-ES', { weekday: 'short' });
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}

export const SUMMARY_DIVIDER_ID = 'summary-divider';
export const SUMMARY_DIVIDER_TEXT = 'Los mensajes anteriores se han resumido para el asistente';

// The model only sees a summary of the messages up to `uptoId`: mark the point
// above the first message it still reads in full. Every message stays visible.
export function withSummaryDivider(messages: ChatMessage[], uptoId: number | null): ChatMessage[] {
  if (uptoId == null) return messages;
  const firstLoadedAfter = messages.findIndex((m) => m.serverId != null && m.serverId > uptoId);
  // Only draw it when the summarised part is on screen too.
  const hasOlder = messages.some((m) => m.serverId != null && m.serverId <= uptoId);
  if (firstLoadedAfter <= 0 || !hasOlder) return messages;
  const divider: ChatMessage = {
    id: SUMMARY_DIVIDER_ID,
    role: 'assistant',
    text: SUMMARY_DIVIDER_TEXT,
    ts: messages[firstLoadedAfter].ts,
    messageType: 'system_notice',
  };
  return [...messages.slice(0, firstLoadedAfter), divider, ...messages.slice(firstLoadedAfter)];
}
